// app/src/lib/idbKv.js — minimal promisified IndexedDB key/value store.
// Used for: per-device crypto salt, Cognito tokens, dexie-restore checkpoint.
//
// Deliberately NOT using Dexie for these — they must remain available even
// when the Dexie schema is being reset/restored.

const DB_NAME = 'crew-kv';
const STORE = 'kv';
const VERSION = 1;

/** @type {Promise<IDBDatabase> | null} */
let openPromise = null;

function open() {
  if (openPromise) return openPromise;
  openPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      if (!req.result.objectStoreNames.contains(STORE)) req.result.createObjectStore(STORE);
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  }).catch((err) => {
    // Don't pin a rejected promise — let future callers retry.
    openPromise = null;
    throw err;
  });
  return openPromise;
}

function tx(mode) {
  return open().then((db) => db.transaction(STORE, mode).objectStore(STORE));
}

/** @param {string} key */
export async function get(key) {
  const store = await tx('readonly');
  return new Promise((resolve, reject) => {
    const r = store.get(key);
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

/** @param {string} key @param {any} value */
export async function set(key, value) {
  const store = await tx('readwrite');
  return new Promise((resolve, reject) => {
    const r = store.put(value, key);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}

/** @param {string} key */
export async function del(key) {
  const store = await tx('readwrite');
  return new Promise((resolve, reject) => {
    const r = store.delete(key);
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}

export async function clearAll() {
  const store = await tx('readwrite');
  return new Promise((resolve, reject) => {
    const r = store.clear();
    r.onsuccess = () => resolve();
    r.onerror = () => reject(r.error);
  });
}
